import React, { useEffect, useState, useCallback, useRef } from "react";
import { supabase } from "../supabase";
import "./flowline-theme.css";
import { withUploadType } from "../uploadType";
import { blockUser, reportContent, unblockUser } from "../moderation";

const API_BASE_URL = process.env.REACT_APP_API_URL || "https://backend-long-morning-8596.fly.dev";
const BUCKET = "ski-videos";
const PAGE_LIMIT = 12;
const NOTIFICATION_POLL_MS = 10000;

async function apiFetch(path, options = {}) {
  const { data } = await supabase.auth.getSession();
  const token = data?.session?.access_token;
  if (!token) {
    throw new Error("You need to be signed in to use Social Feed.");
  }
  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });
  if (!response.ok) {
    let detail = response.statusText;
    try {
      const body = await response.json();
      detail = body.detail || detail;
    } catch (e) {}
    const err = new Error(detail);
    err.status = response.status;
    throw err;
  }
  if (response.status === 204) return null;
  return response.json();
}

function fileExtension(filename) {
  const parts = filename.split(".");
  return parts.length > 1 ? parts[parts.length - 1] : "mp4";
}

function initials(name) {
  if (!name) return "?";
  const chars = name.trim().split(/\s+/).slice(0, 2).map((part) => part[0]?.toUpperCase() || "");
  return chars.join("") || "?";
}

function formatRelativeTime(isoString) {
  const then = new Date(isoString);
  const diffMs = Date.now() - then.getTime();
  const diffMins = Math.round(diffMs / 60000);
  if (diffMins < 60) return diffMins <= 1 ? "just now" : `${diffMins} min ago`;
  const diffHours = Math.round(diffMins / 60);
  if (diffHours < 24) return diffHours === 1 ? "1 hour ago" : `${diffHours} hours ago`;
  const diffDays = Math.round(diffHours / 24);
  if (diffDays === 1) return "yesterday";
  if (diffDays < 7) return `${diffDays} days ago`;
  return then.toLocaleDateString();
}

function notificationText(type) {
  switch (type) {
    case "follow":
      return "followed you";
    case "like":
      return "liked your post";
    case "comment":
      return "commented on your post";
    default:
      return "";
  }
}

function followingMapFromPosts(postsList) {
  const map = {};
  for (const p of postsList) {
    map[p.user_id] = p.is_following_author;
  }
  return map;
}

function AuthorAvatar({ url, name, size }) {
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: Math.max(8, Math.round(size / 4)),
        overflow: "hidden",
        flexShrink: 0,
        background: url ? "transparent" : "linear-gradient(160deg,#1c3550 0%,#122032 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {url ? (
        <img src={url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
      ) : (
        <span style={{ color: "rgba(255,255,255,0.35)", fontWeight: "bold", fontSize: Math.round(size / 2.4) }}>
          {initials(name)}
        </span>
      )}
    </div>
  );
}

function IconHeart({ filled }) {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth={filled ? 0 : 1.8}>
      <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
    </svg>
  );
}

function IconComment() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12c0 4.418-4.03 8-9 8-1.324 0-2.582-.246-3.72-.688L4 20l.9-3.6C3.34 15.03 3 13.56 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
    </svg>
  );
}

function IconVideo() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2.5" y="6" width="13" height="12" rx="2.2" />
      <path d="M15.5 10.4l5-3v9.2l-5-3" strokeLinejoin="round" />
    </svg>
  );
}

function IconImage() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="16" rx="2.2" />
      <circle cx="8.5" cy="9.5" r="1.5" />
      <path d="M21 15.5l-5-5-4 4-3-3-6 6" />
    </svg>
  );
}

function IconClose() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
      <path d="M6 6L18 18M18 6L6 18" stroke="#ffffff" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  );
}

function IconTrash() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 7h16M9 7V4.8c0-.44.36-.8.8-.8h4.4c.44 0 .8.36.8.8V7m-9 0l.6 12.4c.03.6.5 1.1 1.1 1.1h6.6c.6 0 1.07-.5 1.1-1.1L18 7" />
      <path d="M10 11v6M14 11v6" />
    </svg>
  );
}

function FeedPostCard({
  post,
  isExpanded,
  comments,
  commentDraft,
  commentSubmitting,
  currentUserId,
  followSubmitting,
  isFollowingAuthor,
  onToggleLike,
  onToggleComments,
  onDraftChange,
  onAddComment,
  onToggleFollow,
  onOpenProfile,
  onDeletePost,
  onReport,
  isLikersExpanded,
  likers,
  likersLoading,
  likerFollowSubmitting,
  onToggleLikers,
  onToggleLikerFollow,
}) {
  return (
    <div className="fl-card" style={styles.card}>
      <div style={styles.authorRow}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
          <div
            style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0, cursor: "pointer" }}
            onClick={() => onOpenProfile(post.user_id)}
          >
            <AuthorAvatar url={post.author_avatar_url} name={post.author_display_name} size={40} />
            <span style={styles.authorName}>{post.author_display_name}</span>
          </div>
          {currentUserId && post.user_id !== currentUserId && (
            <button
              className="fl-btn"
              style={isFollowingAuthor ? styles.followButtonActive : styles.followButton}
              onClick={() => onToggleFollow(post)}
              disabled={followSubmitting}
              aria-label={isFollowingAuthor ? "Unfollow" : "Follow"}
            >
              {isFollowingAuthor ? "Following" : "Follow"}
            </button>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
          <span style={styles.postTime}>{formatRelativeTime(post.created_at)}</span>
          {currentUserId && post.user_id !== currentUserId && (
            <button type="button" style={styles.reportLink} onClick={() => onReport("post", post.id)}>
              Report
            </button>
          )}
          {currentUserId && post.user_id === currentUserId && (
            <button
              type="button"
              className="fl-btn"
              style={styles.deleteIconButton}
              onClick={() => onDeletePost(post)}
              aria-label="Delete post"
            >
              <IconTrash />
            </button>
          )}
        </div>
      </div>

      {post.photo_url ? (
        <div style={styles.mediaWrap}>
          <img src={post.photo_url} alt="" style={styles.media} />
        </div>
      ) : post.video_url ? (
        <div style={styles.mediaWrap}>
          <video
            src={post.video_url}
            poster={post.thumbnail_url || undefined}
            controls
            playsInline
            style={styles.media}
          />
        </div>
      ) : null}

      {(post.run_name || post.score != null) && (
        <div style={styles.badgeRow}>
          {post.run_name && <span style={styles.runBadge}>{post.run_name}</span>}
          {post.score != null && (
            <span style={styles.scoreBadge}>
              {post.score_label ? `${post.score_label}: ` : "Score: "}
              {post.score}
            </span>
          )}
        </div>
      )}

      {post.caption && <p style={styles.captionText}>{post.caption}</p>}

      <div style={styles.actionsRow}>
        <div style={post.liked_by_me ? styles.likeButtonActive : styles.likeButton}>
          <button
            type="button"
            className="fl-btn fl-like-icon-btn"
            style={styles.likeIconButton}
            onClick={() => onToggleLike(post)}
            aria-label={post.liked_by_me ? "Unlike" : "Like"}
          >
            <IconHeart filled={post.liked_by_me} />
          </button>
          <button
            type="button"
            className="fl-like-count-btn"
            style={styles.likeCountButton}
            onClick={() => onToggleLikers(post.id)}
            aria-label="Show who liked this"
          >
            {post.like_count}
          </button>
        </div>
        <button
          className="fl-btn"
          style={styles.commentButton}
          onClick={() => onToggleComments(post.id)}
          aria-label="Toggle comments"
        >
          <IconComment />
          <span>{post.comment_count}</span>
        </button>
      </div>

      {isExpanded && (
        <div style={styles.commentSection}>
          {comments.length === 0 ? (
            <p style={styles.muted}>No comments yet.</p>
          ) : (
            comments.map((c, idx) => (
              <div
                key={c.id}
                style={{
                  ...styles.commentRow,
                  ...(idx === comments.length - 1 ? { borderBottom: "none" } : {}),
                }}
              >
                <AuthorAvatar url={c.author_avatar_url} name={c.author_display_name} size={28} />
                <div style={styles.commentBody}>
                  <span style={styles.commentAuthor}>{c.author_display_name}</span>
                  <span style={styles.commentText}>{c.body}</span>
                  {currentUserId && c.user_id !== currentUserId && (
                    <button
                      type="button"
                      style={{ ...styles.reportLink, alignSelf: "flex-start" }}
                      onClick={() => onReport("comment", c.id)}
                    >
                      Report
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            <input
              style={{ ...styles.input, flex: 1 }}
              value={commentDraft}
              onChange={(e) => onDraftChange(post.id, e.target.value)}
              placeholder="Add a comment..."
              maxLength={1000}
            />
            <button
              style={styles.primaryButton}
              onClick={() => onAddComment(post.id)}
              disabled={commentSubmitting || !commentDraft.trim()}
            >
              Post
            </button>
          </div>
        </div>
      )}

      {isLikersExpanded && (
        <div style={styles.modalOverlay} onClick={() => onToggleLikers(post.id)}>
          <div
            style={{ ...styles.modalContent, maxHeight: "80vh", overflowY: "auto" }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0 }}>Likes</h3>
            {likersLoading ? (
              <p style={styles.muted}>Loading...</p>
            ) : likers.length === 0 ? (
              <p style={styles.muted}>No likes yet.</p>
            ) : (
              likers.map((liker, idx) => (
                <div
                  key={liker.user_id}
                  style={{
                    ...styles.listRow,
                    ...(idx === likers.length - 1 ? { borderBottom: "none" } : {}),
                  }}
                >
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0, cursor: "pointer" }}
                    onClick={() => {
                      onToggleLikers(post.id);
                      onOpenProfile(liker.user_id);
                    }}
                  >
                    <AuthorAvatar url={liker.avatar_url} name={liker.display_name} size={28} />
                    <span style={styles.commentAuthor}>{liker.display_name}</span>
                  </div>
                  {currentUserId && liker.user_id !== currentUserId && (
                    <button
                      className="fl-btn"
                      style={liker.is_following ? styles.followButtonActive : styles.followButton}
                      onClick={() => onToggleLikerFollow(post.id, liker)}
                      disabled={!!likerFollowSubmitting[liker.user_id]}
                      aria-label={liker.is_following ? "Unfollow" : "Follow"}
                    >
                      {liker.is_following ? "Following" : "Follow"}
                    </button>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function UserProfileView({ userId, currentUserId, onBack, onEditProfile, onBlockChange }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [profile, setProfile] = useState(null);
  const [followSubmitting, setFollowSubmitting] = useState(false);

  const [posts, setPosts] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [lightboxPost, setLightboxPost] = useState(null);

  const loadProfile = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [profileData, postsData] = await Promise.all([
        apiFetch(`/social-feed/users/${userId}`),
        apiFetch(`/social-feed/users/${userId}/posts?limit=${PAGE_LIMIT}`),
      ]);
      setProfile(profileData);
      setPosts(postsData);
      setHasMore(postsData.length === PAGE_LIMIT);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  async function handleLoadMore() {
    if (loadingMore || posts.length === 0) return;
    setLoadingMore(true);
    setError(null);
    try {
      const before = posts[posts.length - 1].created_at;
      const data = await apiFetch(
        `/social-feed/users/${userId}/posts?limit=${PAGE_LIMIT}&before=${encodeURIComponent(before)}`
      );
      setPosts((prev) => [...prev, ...data]);
      setHasMore(data.length === PAGE_LIMIT);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingMore(false);
    }
  }

  async function handleToggleFollow() {
    const wasFollowing = profile.is_following;
    setFollowSubmitting(true);
    setProfile((prev) => ({ ...prev, is_following: !wasFollowing }));
    try {
      await apiFetch(`/social-feed/users/${userId}/follow`, {
        method: wasFollowing ? "DELETE" : "POST",
      });
    } catch (err) {
      setProfile((prev) => ({ ...prev, is_following: wasFollowing }));
      setError(err.message);
    } finally {
      setFollowSubmitting(false);
    }
  }

  async function handleDeletePost(post) {
    if (!window.confirm("Delete this post? This can't be undone.")) return;
    try {
      await apiFetch(`/social-feed/posts/${post.id}`, { method: "DELETE" });
      setPosts((prev) => prev.filter((p) => p.id !== post.id));
      setLightboxPost((prev) => (prev && prev.id === post.id ? null : prev));
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleToggleBlock() {
    if (profile.blocked_by_me) {
      if (await unblockUser(apiFetch, userId, profile.display_name)) {
        onBlockChange(userId, false);
        loadProfile();
      }
    } else if (await blockUser(apiFetch, userId, profile.display_name)) {
      setProfile((prev) => ({ ...prev, blocked_by_me: true }));
      setPosts([]);
      setHasMore(false);
      setLightboxPost(null);
      onBlockChange(userId, true);
    }
  }

  if (loading) {
    return (
      <div className="fl-theme" style={styles.page}>
        <p style={styles.muted}>Loading profile...</p>
      </div>
    );
  }

  return (
    <div className="fl-theme" style={styles.page}>
      <div style={styles.profileViewHeader}>
        <button style={styles.secondaryButton} onClick={onBack}>
          Back
        </button>
        {currentUserId === userId && (
          <button className="fl-btn" style={styles.secondaryButton} onClick={onEditProfile}>
            Edit Profile
          </button>
        )}
      </div>

      {error && <div style={styles.errorBanner}>{error}</div>}

      {profile && (
        <div style={styles.profileInfoCard}>
          <div style={styles.profileHeaderRow}>
            <AuthorAvatar url={profile.avatar_url} name={profile.display_name} size={80} />
            <div style={{ minWidth: 0 }}>
              <h2 style={styles.profileName}>{profile.display_name}</h2>
              {profile.bio && <p style={styles.profileBio}>{profile.bio}</p>}
              <div style={styles.profileStatsRow}>
                <span>
                  <strong>{profile.follower_count}</strong> followers
                </span>
                <span>
                  <strong>{profile.following_count}</strong> following
                </span>
              </div>
              {currentUserId && userId !== currentUserId && (
                <button
                  className="fl-btn"
                  style={profile.is_following ? styles.followButtonActive : styles.followButton}
                  onClick={handleToggleFollow}
                  disabled={followSubmitting}
                  aria-label={profile.is_following ? "Unfollow" : "Follow"}
                >
                  {profile.is_following ? "Following" : "Follow"}
                </button>
              )}
              {currentUserId && userId !== currentUserId && (
                <div style={{ display: "flex", gap: 12, marginTop: 10 }}>
                  <button type="button" style={styles.reportLink} onClick={() => reportContent(apiFetch, "profile", userId)}>
                    Report
                  </button>
                  <button type="button" style={styles.reportLink} onClick={handleToggleBlock}>
                    {profile.blocked_by_me ? "Unblock" : "Block"}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {profile?.blocked_by_me ? (
        <div className="fl-card" style={styles.card}>
          <p>You blocked this user. You won't see their posts, comments or messages.</p>
        </div>
      ) : posts.length === 0 ? (
        <div className="fl-card" style={styles.card}>
          <p>No posts yet.</p>
        </div>
      ) : (
        <div style={styles.profileGrid}>
          {posts.map((post) => (
            <div
              key={post.id}
              style={{ ...styles.profileGridTile, cursor: "pointer" }}
              onClick={() => setLightboxPost(post)}
            >
              {post.photo_url ? (
                <img src={post.photo_url} alt="" style={styles.profileGridMedia} />
              ) : (
                <video
                  src={post.video_url}
                  poster={post.thumbnail_url || undefined}
                  muted
                  playsInline
                  style={styles.profileGridMedia}
                />
              )}
            </div>
          ))}
        </div>
      )}

      {lightboxPost && (
        <div style={styles.lightboxOverlay} onClick={() => setLightboxPost(null)}>
          <button
            style={styles.lightboxCloseButton}
            onClick={() => setLightboxPost(null)}
            aria-label="Close"
          >
            ×
          </button>
          {currentUserId === userId && (
            <button
              style={styles.lightboxDeleteButton}
              onClick={(e) => {
                e.stopPropagation();
                handleDeletePost(lightboxPost);
              }}
              aria-label="Delete post"
            >
              Delete
            </button>
          )}
          {lightboxPost.photo_url ? (
            <img src={lightboxPost.photo_url} alt="" style={styles.lightboxMedia} />
          ) : (
            <video
              src={lightboxPost.video_url}
              poster={lightboxPost.thumbnail_url || undefined}
              controls
              autoPlay
              playsInline
              style={styles.lightboxMedia}
              onClick={(e) => e.stopPropagation()}
            />
          )}
        </div>
      )}

      {hasMore && (
        <div style={{ textAlign: "center", marginTop: 8 }}>
          <button style={styles.secondaryButton} onClick={handleLoadMore} disabled={loadingMore}>
            {loadingMore ? "Loading..." : "Load more"}
          </button>
        </div>
      )}
    </div>
  );
}

export default function SocialFeedMode({ onEditProfile }) {
  const [loading, setLoading] = useState(true);
  const [posts, setPosts] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [feedView, setFeedView] = useState("all");

  const [viewingUserId, setViewingUserId] = useState(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [searchFollowSubmitting, setSearchFollowSubmitting] = useState({});

  const [currentUserId, setCurrentUserId] = useState(null);
  const [followSubmitting, setFollowSubmitting] = useState({});
  const [followingByAuthor, setFollowingByAuthor] = useState({});

  const [expandedComments, setExpandedComments] = useState({});
  const [commentsByPost, setCommentsByPost] = useState({});
  const [commentDrafts, setCommentDrafts] = useState({});
  const [commentSubmitting, setCommentSubmitting] = useState({});

  const [expandedLikers, setExpandedLikers] = useState({});
  const [likersByPost, setLikersByPost] = useState({});
  const [likersLoading, setLikersLoading] = useState({});
  const [likerFollowSubmitting, setLikerFollowSubmitting] = useState({});

  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [notificationsLoadingMore, setNotificationsLoadingMore] = useState(false);
  const [notificationsHasMore, setNotificationsHasMore] = useState(false);
  const [notificationsError, setNotificationsError] = useState(null);
  const notificationsMountedRef = useRef(true);

  const [showShareModal, setShowShareModal] = useState(false);
  const [shareMediaType, setShareMediaType] = useState("video");
  const [shareFile, setShareFile] = useState(null);
  const [sharePreviewUrl, setSharePreviewUrl] = useState(null);
  const [shareCaption, setShareCaption] = useState("");
  const [shareRunName, setShareRunName] = useState("");
  const [shareScore, setShareScore] = useState("");
  const [uploading, setUploading] = useState(false);
  const [shareError, setShareError] = useState(null);
  const shareFileInputRef = useRef(null);

  useEffect(() => {
    if (!shareFile) {
      setSharePreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(shareFile);
    setSharePreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [shareFile]);

  const loadFeed = useCallback(async (view) => {
    setLoading(true);
    setError(null);
    try {
      const followingParam = view === "following" ? "&following_only=true" : "";
      const data = await apiFetch(`/social-feed/posts?limit=${PAGE_LIMIT}${followingParam}`);
      setPosts(data);
      setFollowingByAuthor((prev) => ({ ...prev, ...followingMapFromPosts(data) }));
      setHasMore(data.length === PAGE_LIMIT);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFeed(feedView);
  }, [loadFeed, feedView]);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setCurrentUserId(data?.session?.user?.id || null);
    });
  }, []);

  const pollUnreadNotificationCount = useCallback(async () => {
    const { data } = await supabase.auth.getSession();
    if (!data?.session) {
      if (notificationsMountedRef.current) setUnreadNotificationCount(0);
      return;
    }
    try {
      const result = await apiFetch("/social-feed/notifications/unread-count");
      if (notificationsMountedRef.current) setUnreadNotificationCount(result.unread_count || 0);
    } catch (err) {
      // ignore transient polling errors
    }
  }, []);

  useEffect(() => {
    notificationsMountedRef.current = true;
    pollUnreadNotificationCount();
    const interval = setInterval(pollUnreadNotificationCount, NOTIFICATION_POLL_MS);
    return () => {
      notificationsMountedRef.current = false;
      clearInterval(interval);
    };
  }, [pollUnreadNotificationCount]);

  useEffect(() => {
    const trimmed = searchQuery.trim();
    if (trimmed.length < 2) {
      setSearchResults([]);
      setSearchLoading(false);
      setSearchError(null);
      return;
    }
    setSearchLoading(true);
    setSearchError(null);
    const timeoutId = setTimeout(async () => {
      try {
        const data = await apiFetch(
          `/social-feed/users/search?q=${encodeURIComponent(trimmed)}&limit=20`
        );
        setSearchResults(data);
      } catch (err) {
        setSearchError(err.message);
      } finally {
        setSearchLoading(false);
      }
    }, 350);
    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  async function handleLoadMore() {
    if (loadingMore || posts.length === 0) return;
    setLoadingMore(true);
    setError(null);
    try {
      const before = posts[posts.length - 1].created_at;
      const followingParam = feedView === "following" ? "&following_only=true" : "";
      const data = await apiFetch(
        `/social-feed/posts?limit=${PAGE_LIMIT}&before=${encodeURIComponent(before)}${followingParam}`
      );
      setPosts((prev) => [...prev, ...data]);
      setFollowingByAuthor((prev) => ({ ...prev, ...followingMapFromPosts(data) }));
      setHasMore(data.length === PAGE_LIMIT);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingMore(false);
    }
  }

  async function handleOpenNotifications() {
    setShowNotifications(true);
    setNotificationsLoading(true);
    setNotificationsError(null);
    try {
      const [data] = await Promise.all([
        apiFetch(`/social-feed/notifications?limit=${PAGE_LIMIT}`),
        apiFetch("/social-feed/notifications/read", { method: "POST" }),
      ]);
      setNotifications(data);
      setNotificationsHasMore(data.length === PAGE_LIMIT);
      setUnreadNotificationCount(0);
    } catch (err) {
      setNotificationsError(err.message);
    } finally {
      setNotificationsLoading(false);
    }
  }

  async function handleLoadMoreNotifications() {
    if (notificationsLoadingMore || notifications.length === 0) return;
    setNotificationsLoadingMore(true);
    setNotificationsError(null);
    try {
      const before = notifications[notifications.length - 1].created_at;
      const data = await apiFetch(
        `/social-feed/notifications?limit=${PAGE_LIMIT}&before=${encodeURIComponent(before)}`
      );
      setNotifications((prev) => [...prev, ...data]);
      setNotificationsHasMore(data.length === PAGE_LIMIT);
    } catch (err) {
      setNotificationsError(err.message);
    } finally {
      setNotificationsLoadingMore(false);
    }
  }

  async function handleToggleLike(post) {
    const wasLiked = post.liked_by_me;
    const prevCount = post.like_count;
    setPosts((prev) =>
      prev.map((p) =>
        p.id === post.id
          ? { ...p, liked_by_me: !wasLiked, like_count: prevCount + (wasLiked ? -1 : 1) }
          : p
      )
    );
    try {
      const result = await apiFetch(`/social-feed/posts/${post.id}/like`, { method: "POST" });
      setPosts((prev) =>
        prev.map((p) =>
          p.id === post.id ? { ...p, liked_by_me: result.liked, like_count: result.like_count } : p
        )
      );
    } catch (err) {
      setPosts((prev) =>
        prev.map((p) => (p.id === post.id ? { ...p, liked_by_me: wasLiked, like_count: prevCount } : p))
      );
      setError(err.message);
    }
  }

  async function handleToggleFollow(post) {
    const authorId = post.user_id;
    const wasFollowing = followingByAuthor[authorId];
    setFollowSubmitting((prev) => ({ ...prev, [authorId]: true }));
    setFollowingByAuthor((prev) => ({ ...prev, [authorId]: !wasFollowing }));
    try {
      await apiFetch(`/social-feed/users/${authorId}/follow`, {
        method: wasFollowing ? "DELETE" : "POST",
      });
    } catch (err) {
      setFollowingByAuthor((prev) => ({ ...prev, [authorId]: wasFollowing }));
      setError(err.message);
    } finally {
      setFollowSubmitting((prev) => ({ ...prev, [authorId]: false }));
    }
  }

  async function handleToggleSearchFollow(result) {
    const wasFollowing = result.is_following;
    setSearchFollowSubmitting((prev) => ({ ...prev, [result.user_id]: true }));
    setSearchResults((prev) =>
      prev.map((r) => (r.user_id === result.user_id ? { ...r, is_following: !wasFollowing } : r))
    );
    try {
      await apiFetch(`/social-feed/users/${result.user_id}/follow`, {
        method: wasFollowing ? "DELETE" : "POST",
      });
    } catch (err) {
      setSearchResults((prev) =>
        prev.map((r) => (r.user_id === result.user_id ? { ...r, is_following: wasFollowing } : r))
      );
      setError(err.message);
    } finally {
      setSearchFollowSubmitting((prev) => ({ ...prev, [result.user_id]: false }));
    }
  }

  async function handleToggleLikerFollow(postId, liker) {
    const wasFollowing = liker.is_following;
    setLikerFollowSubmitting((prev) => ({ ...prev, [liker.user_id]: true }));
    setLikersByPost((prev) => ({
      ...prev,
      [postId]: (prev[postId] || []).map((l) =>
        l.user_id === liker.user_id ? { ...l, is_following: !wasFollowing } : l
      ),
    }));
    try {
      await apiFetch(`/social-feed/users/${liker.user_id}/follow`, {
        method: wasFollowing ? "DELETE" : "POST",
      });
    } catch (err) {
      setLikersByPost((prev) => ({
        ...prev,
        [postId]: (prev[postId] || []).map((l) =>
          l.user_id === liker.user_id ? { ...l, is_following: wasFollowing } : l
        ),
      }));
      setError(err.message);
    } finally {
      setLikerFollowSubmitting((prev) => ({ ...prev, [liker.user_id]: false }));
    }
  }

  async function handleToggleComments(postId) {
    const isExpanding = !expandedComments[postId];
    setExpandedComments((prev) => ({ ...prev, [postId]: isExpanding }));
    if (isExpanding && !commentsByPost[postId]) {
      try {
        const data = await apiFetch(`/social-feed/posts/${postId}/comments`);
        setCommentsByPost((prev) => ({ ...prev, [postId]: data }));
      } catch (err) {
        setError(err.message);
      }
    }
  }

  async function handleToggleLikers(postId) {
    const isExpanding = !expandedLikers[postId];
    setExpandedLikers((prev) => ({ ...prev, [postId]: isExpanding }));
    if (isExpanding && !likersByPost[postId]) {
      setLikersLoading((prev) => ({ ...prev, [postId]: true }));
      try {
        const data = await apiFetch(`/social-feed/posts/${postId}/likes`);
        setLikersByPost((prev) => ({ ...prev, [postId]: data }));
      } catch (err) {
        setError(err.message);
      } finally {
        setLikersLoading((prev) => ({ ...prev, [postId]: false }));
      }
    }
  }

  async function handleAddComment(postId) {
    const body = (commentDrafts[postId] || "").trim();
    if (!body || commentSubmitting[postId]) return;
    setCommentSubmitting((prev) => ({ ...prev, [postId]: true }));
    try {
      const comment = await apiFetch(`/social-feed/posts/${postId}/comments`, {
        method: "POST",
        body: JSON.stringify({ body }),
      });
      setCommentsByPost((prev) => ({ ...prev, [postId]: [...(prev[postId] || []), comment] }));
      setCommentDrafts((prev) => ({ ...prev, [postId]: "" }));
      setPosts((prev) =>
        prev.map((p) => (p.id === postId ? { ...p, comment_count: p.comment_count + 1 } : p))
      );
    } catch (err) {
      setError(err.message);
    } finally {
      setCommentSubmitting((prev) => ({ ...prev, [postId]: false }));
    }
  }

  async function handleDeletePost(post) {
    if (!window.confirm("Delete this post? This can't be undone.")) return;
    try {
      await apiFetch(`/social-feed/posts/${post.id}`, { method: "DELETE" });
      setPosts((prev) => prev.filter((p) => p.id !== post.id));
    } catch (err) {
      setError(err.message);
    }
  }

  function handleReport(targetType, targetId) {
    reportContent(apiFetch, targetType, targetId);
  }

  function handleBlockChange(userId, blocked) {
    if (!blocked) {
      loadFeed(feedView);
      return;
    }
    // Blocked: drop their posts and comments now; the backend leaves them out from here on.
    setPosts((prev) => prev.filter((p) => p.user_id !== userId));
    setCommentsByPost((prev) =>
      Object.fromEntries(Object.entries(prev).map(([postId, list]) => [postId, list.filter((c) => c.user_id !== userId)]))
    );
  }

  function resetShareForm() {
    setShareMediaType("video");
    setShareFile(null);
    setShareCaption("");
    setShareRunName("");
    setShareScore("");
    setShareError(null);
  }

  function handleMediaTypeChange(type) {
    if (type === shareMediaType || uploading) return;
    setShareMediaType(type);
    setShareFile(null);
    if (shareFileInputRef.current) shareFileInputRef.current.value = "";
  }

  function handleShareFileChange(e) {
    setShareFile(e.target.files?.[0] || null);
  }

  function handleRemoveShareFile(e) {
    e.preventDefault();
    e.stopPropagation();
    setShareFile(null);
    if (shareFileInputRef.current) shareFileInputRef.current.value = "";
  }

  async function handleShareSubmit(e) {
    e.preventDefault();
    if (!shareFile) {
      setShareError(`Please choose a ${shareMediaType} to share.`);
      return;
    }
    setUploading(true);
    setShareError(null);
    try {
      const ext = fileExtension(shareFile.name);
      const { upload_url, path, token } = await apiFetch("/social-feed/upload-url", {
        method: "POST",
        body: JSON.stringify({ file_extension: ext }),
      });

      const { error: uploadError } = await supabase.storage
        .from(BUCKET)
        .uploadToSignedUrl(path, token, withUploadType(shareFile));
      if (uploadError) {
        throw new Error(uploadError.message || "Upload failed.");
      }
      void upload_url;

      const newPost = await apiFetch("/social-feed/posts", {
        method: "POST",
        body: JSON.stringify({
          [shareMediaType === "photo" ? "photo_path" : "video_path"]: path,
          caption: shareCaption.trim() || null,
          run_name: shareRunName.trim() || null,
          score: shareScore.trim() ? parseFloat(shareScore.trim()) : null,
        }),
      });

      setPosts((prev) => [newPost, ...prev]);
      setFollowingByAuthor((prev) => ({ ...prev, ...followingMapFromPosts([newPost]) }));
      resetShareForm();
      setShowShareModal(false);
    } catch (err) {
      setShareError(err.message);
    } finally {
      setUploading(false);
    }
  }

  if (viewingUserId) {
    return (
      <UserProfileView
        userId={viewingUserId}
        currentUserId={currentUserId}
        onBack={() => setViewingUserId(null)}
        onEditProfile={onEditProfile}
        onBlockChange={handleBlockChange}
      />
    );
  }

  if (loading) {
    return (
      <div className="fl-theme" style={styles.page}>
        <p style={styles.muted}>Loading feed...</p>
      </div>
    );
  }

  return (
    <div className="fl-theme" style={styles.page}>
      <div style={styles.header}>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <button
            style={styles.secondaryButton}
            onClick={() => currentUserId && setViewingUserId(currentUserId)}
          >
            Profile
          </button>
          <div style={{ position: "relative" }}>
            <button style={styles.secondaryButton} onClick={handleOpenNotifications}>
              Notifications
            </button>
            {unreadNotificationCount > 0 && (
              <span style={styles.notificationBadge}>
                {unreadNotificationCount > 99 ? "99+" : unreadNotificationCount}
              </span>
            )}
          </div>
          <button
            style={styles.primaryButton}
            onClick={() => {
              resetShareForm();
              setShowShareModal(true);
            }}
          >
            Share a run
          </button>
        </div>
      </div>

      {error && <div style={styles.errorBanner}>{error}</div>}

      <div style={styles.searchSection}>
        <input
          style={styles.input}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Find people by name..."
        />
        {searchError && <div style={styles.errorBanner}>{searchError}</div>}
        {searchQuery.trim().length >= 2 &&
          (searchLoading ? (
            <p style={styles.muted}>Searching...</p>
          ) : searchResults.length === 0 ? (
            <div className="fl-card" style={styles.card}>
              <p>No users found.</p>
            </div>
          ) : (
            <div style={styles.searchResultsList}>
              {searchResults.map((result, idx) => (
                <div
                  key={result.user_id}
                  style={{
                    ...styles.listRow,
                    ...(idx === searchResults.length - 1 ? { borderBottom: "none" } : {}),
                  }}
                >
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0, cursor: "pointer" }}
                    onClick={() => setViewingUserId(result.user_id)}
                  >
                    <AuthorAvatar url={result.avatar_url} name={result.display_name} size={40} />
                    <span style={styles.authorName}>{result.display_name}</span>
                  </div>
                  {currentUserId && result.user_id !== currentUserId && (
                    <button
                      className="fl-btn"
                      style={result.is_following ? styles.followButtonActive : styles.followButton}
                      onClick={() => handleToggleSearchFollow(result)}
                      disabled={!!searchFollowSubmitting[result.user_id]}
                      aria-label={result.is_following ? "Unfollow" : "Follow"}
                    >
                      {result.is_following ? "Following" : "Follow"}
                    </button>
                  )}
                </div>
              ))}
            </div>
          ))}
      </div>

      <div style={styles.tabRow}>
        <button style={feedView === "all" ? styles.tabActive : styles.tab} onClick={() => setFeedView("all")}>
          All
        </button>
        <button
          style={feedView === "following" ? styles.tabActive : styles.tab}
          onClick={() => setFeedView("following")}
        >
          Following
        </button>
      </div>

      {posts.length === 0 ? (
        <div className="fl-card" style={styles.card}>
          <p>
            {feedView === "following"
              ? "You're not following anyone yet — follow people from their posts or profile to see them here."
              : "No posts yet — be the first to share a run!"}
          </p>
        </div>
      ) : (
        posts.map((post) => (
          <FeedPostCard
            key={post.id}
            post={post}
            isExpanded={!!expandedComments[post.id]}
            comments={commentsByPost[post.id] || []}
            commentDraft={commentDrafts[post.id] || ""}
            commentSubmitting={!!commentSubmitting[post.id]}
            currentUserId={currentUserId}
            followSubmitting={!!followSubmitting[post.user_id]}
            isFollowingAuthor={!!followingByAuthor[post.user_id]}
            onToggleLike={handleToggleLike}
            onToggleComments={handleToggleComments}
            onDraftChange={(postId, value) =>
              setCommentDrafts((prev) => ({ ...prev, [postId]: value }))
            }
            onAddComment={handleAddComment}
            onToggleFollow={handleToggleFollow}
            onOpenProfile={setViewingUserId}
            onDeletePost={handleDeletePost}
            onReport={handleReport}
            isLikersExpanded={!!expandedLikers[post.id]}
            likers={likersByPost[post.id] || []}
            likersLoading={!!likersLoading[post.id]}
            likerFollowSubmitting={likerFollowSubmitting}
            onToggleLikers={handleToggleLikers}
            onToggleLikerFollow={handleToggleLikerFollow}
          />
        ))
      )}

      {hasMore && (
        <div style={{ textAlign: "center", marginTop: 8 }}>
          <button style={styles.secondaryButton} onClick={handleLoadMore} disabled={loadingMore}>
            {loadingMore ? "Loading..." : "Load more"}
          </button>
        </div>
      )}

      {showNotifications && (
        <div style={styles.modalOverlay} onClick={() => setShowNotifications(false)}>
          <div
            style={{ ...styles.modalContent, maxHeight: "80vh", overflowY: "auto" }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0 }}>Notifications</h3>
            {notificationsError && <div style={styles.errorBanner}>{notificationsError}</div>}
            {notificationsLoading ? (
              <p style={styles.muted}>Loading...</p>
            ) : notifications.length === 0 ? (
              <p style={styles.muted}>No notifications yet.</p>
            ) : (
              notifications.map((n, idx) => (
                <div
                  key={n.id}
                  style={{
                    ...styles.listRow,
                    cursor: "pointer",
                    ...(idx === notifications.length - 1 ? { borderBottom: "none" } : {}),
                  }}
                  onClick={() => {
                    setShowNotifications(false);
                    setViewingUserId(n.actor_id);
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                    <AuthorAvatar url={n.actor_avatar_url} name={n.actor_name} size={36} />
                    <div style={{ minWidth: 0 }}>
                      <span style={styles.commentAuthor}>{n.actor_name}</span>{" "}
                      <span style={styles.commentText}>{notificationText(n.type)}</span>
                      <div style={styles.postTime}>{formatRelativeTime(n.created_at)}</div>
                    </div>
                  </div>
                </div>
              ))
            )}
            {notificationsHasMore && (
              <div style={{ textAlign: "center", marginTop: 8 }}>
                <button
                  style={styles.secondaryButton}
                  onClick={handleLoadMoreNotifications}
                  disabled={notificationsLoadingMore}
                >
                  {notificationsLoadingMore ? "Loading..." : "Load more"}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {showShareModal && (
        <div style={styles.modalOverlay} onClick={() => !uploading && setShowShareModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>Share a run</h3>
            {shareError && <div style={styles.errorBanner}>{shareError}</div>}
            <form onSubmit={handleShareSubmit}>
              <div style={styles.mediaTypeRow}>
                <button
                  type="button"
                  className={`fl-tag-toggle${shareMediaType === "video" ? " fl-tag-toggle-selected" : ""}`}
                  onClick={() => handleMediaTypeChange("video")}
                  disabled={uploading}
                >
                  Video
                </button>
                <button
                  type="button"
                  className={`fl-tag-toggle${shareMediaType === "photo" ? " fl-tag-toggle-selected" : ""}`}
                  onClick={() => handleMediaTypeChange("photo")}
                  disabled={uploading}
                >
                  Photo
                </button>
              </div>

              <div style={styles.field}>
                <div
                  className="fl-upload-tile"
                  onClick={() => !uploading && shareFileInputRef.current?.click()}
                >
                  {shareFile ? (
                    <>
                      {shareMediaType === "photo" ? (
                        <img src={sharePreviewUrl} alt="" className="fl-upload-preview-media" />
                      ) : (
                        <video
                          src={sharePreviewUrl}
                          className="fl-upload-preview-media"
                          controls
                          muted
                          playsInline
                          onClick={(e) => e.stopPropagation()}
                        />
                      )}
                      <button
                        type="button"
                        className="fl-photo-remove-btn"
                        style={styles.uploadRemoveButton}
                        onClick={handleRemoveShareFile}
                        disabled={uploading}
                        aria-label="Remove file"
                      >
                        <IconClose />
                      </button>
                    </>
                  ) : (
                    <>
                      {shareMediaType === "photo" ? <IconImage /> : <IconVideo />}
                      <span className="fl-upload-tile-label">
                        Add {shareMediaType === "photo" ? "photo" : "video"}
                      </span>
                    </>
                  )}
                  <input
                    ref={shareFileInputRef}
                    type="file"
                    accept={shareMediaType === "photo" ? "image/*" : "video/*"}
                    onChange={handleShareFileChange}
                    disabled={uploading}
                    style={{ display: "none" }}
                  />
                </div>
              </div>

              <div style={styles.field}>
                <label style={styles.label}>Caption (optional)</label>
                <textarea
                  style={{ ...styles.input, minHeight: 60 }}
                  value={shareCaption}
                  onChange={(e) => setShareCaption(e.target.value)}
                  maxLength={500}
                  disabled={uploading}
                  placeholder="How'd the run go?"
                />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Run name (optional)</label>
                <input
                  style={styles.input}
                  value={shareRunName}
                  onChange={(e) => setShareRunName(e.target.value)}
                  maxLength={120}
                  disabled={uploading}
                  placeholder="e.g. Corbet's Couloir"
                />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Score (optional)</label>
                <input
                  style={styles.input}
                  type="number"
                  step="0.1"
                  value={shareScore}
                  onChange={(e) => setShareScore(e.target.value)}
                  disabled={uploading}
                  placeholder="e.g. 8.5"
                />
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                <button type="submit" style={styles.primaryButton} disabled={uploading}>
                  {uploading ? "Uploading..." : "Post"}
                </button>
                <button
                  type="button"
                  style={styles.secondaryButton}
                  onClick={() => setShowShareModal(false)}
                  disabled={uploading}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100%",
    backgroundColor: "#0d1b2a",
    color: "#ffffff",
    fontFamily: "Georgia, 'Times New Roman', serif",
    padding: "24px 16px 64px",
    boxSizing: "border-box",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    maxWidth: 420,
    margin: "0 auto 16px",
  },
  profileViewHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: 10,
    maxWidth: 420,
    margin: "0 auto 16px",
  },
  tabRow: { display: "flex", gap: 8, maxWidth: 420, margin: "0 auto 16px" },
  tab: {
    flex: 1,
    background: "transparent",
    border: "1px solid #1e3a5f",
    color: "#ddddee",
    padding: "8px 16px",
    borderRadius: 8,
    cursor: "pointer",
    fontFamily: "Georgia, 'Times New Roman', serif",
  },
  tabActive: {
    flex: 1,
    background: "#c9a84c",
    border: "1px solid #c9a84c",
    color: "#0d1b2a",
    padding: "8px 16px",
    borderRadius: 8,
    cursor: "pointer",
    fontWeight: "bold",
    fontFamily: "Georgia, 'Times New Roman', serif",
  },
  searchSection: {
    maxWidth: 420,
    margin: "0 auto 16px",
  },
  searchResultsList: {
    display: "flex",
    flexDirection: "column",
    marginTop: 10,
  },
  listRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    padding: "10px 4px",
    borderBottom: "1px solid #1e3a5f",
  },
  notificationBadge: {
    position: "absolute",
    top: -6,
    right: -6,
    minWidth: 14,
    height: 14,
    padding: "0 3px",
    borderRadius: "50%",
    backgroundColor: "#c9a84c",
    border: "1px solid #0d1b2a",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 9,
    fontWeight: 700,
    lineHeight: 1,
    color: "#0d1b2a",
    fontFamily: "Georgia, 'Times New Roman', serif",
  },
  card: {
    padding: 20,
    maxWidth: 420,
    margin: "0 auto 16px",
    boxSizing: "border-box",
  },
  profileInfoCard: {
    border: "1px solid #2c4a68",
    borderRadius: 20,
    padding: 20,
    maxWidth: 420,
    margin: "0 auto 20px",
    boxSizing: "border-box",
  },
  profileHeaderRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: 16,
  },
  profileName: {
    margin: "0 0 6px",
    color: "#eef2f6",
  },
  profileBio: {
    margin: "0 0 12px",
    fontSize: 14,
    lineHeight: 1.5,
    color: "#c7d4e0",
  },
  profileStatsRow: {
    display: "flex",
    gap: 14,
    fontSize: 13,
    color: "#c7d4e0",
    marginBottom: 12,
  },
  profileGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: 6,
    maxWidth: 420,
    margin: "0 auto",
  },
  profileGridTile: {
    position: "relative",
    aspectRatio: "1 / 1",
    borderRadius: 8,
    overflow: "hidden",
    backgroundColor: "#000000",
  },
  profileGridMedia: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },
  lightboxOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.92)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
    boxSizing: "border-box",
    zIndex: 1100,
  },
  lightboxMedia: {
    maxWidth: "90vw",
    maxHeight: "90vh",
    objectFit: "contain",
    borderRadius: 8,
  },
  lightboxCloseButton: {
    position: "absolute",
    top: 20,
    right: 24,
    background: "transparent",
    border: "none",
    color: "#ffffff",
    fontSize: 32,
    lineHeight: 1,
    cursor: "pointer",
  },
  lightboxDeleteButton: {
    position: "absolute",
    top: 20,
    left: 24,
    background: "rgba(255,68,68,0.14)",
    border: "1px solid #ff4444",
    color: "#ff4444",
    borderRadius: 8,
    padding: "8px 14px",
    cursor: "pointer",
    fontFamily: "Georgia, 'Times New Roman', serif",
    fontSize: 13,
    fontWeight: "bold",
  },
  authorRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    marginBottom: 14,
  },
  authorName: {
    fontWeight: "bold",
    fontSize: 15,
    color: "#eef2f6",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  postTime: {
    fontSize: 12,
    color: "#8fa3b8",
    flexShrink: 0,
    whiteSpace: "nowrap",
  },
  mediaWrap: {
    borderRadius: 14,
    overflow: "hidden",
    boxShadow: "0 12px 28px rgba(0,0,0,0.4)",
    backgroundColor: "#000000",
    maxHeight: "70vh",
  },
  media: {
    width: "auto",
    height: "auto",
    maxWidth: "100%",
    maxHeight: "70vh",
    objectFit: "contain",
    margin: "0 auto",
    display: "block",
    backgroundColor: "#000000",
  },
  badgeRow: {
    marginTop: 14,
    display: "flex",
    gap: 8,
    flexWrap: "wrap",
  },
  runBadge: {
    display: "inline-block",
    background: "#c9a84c",
    color: "#0d1b2a",
    fontWeight: "bold",
    textTransform: "uppercase",
    fontSize: 11,
    letterSpacing: "0.06em",
    padding: "4px 10px",
    borderRadius: 999,
  },
  scoreBadge: {
    display: "inline-block",
    background: "#1c3550",
    color: "#c7d4e0",
    fontSize: 11,
    fontWeight: "bold",
    padding: "4px 10px",
    borderRadius: 999,
    border: "1px solid #2c4a68",
  },
  captionText: {
    margin: "12px 0 0",
    fontSize: 14,
    lineHeight: 1.6,
    color: "#c7d4e0",
  },
  actionsRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginTop: 14,
  },
  field: { marginBottom: 16 },
  label: {
    display: "block",
    fontSize: 11,
    fontWeight: "bold",
    letterSpacing: "0.09em",
    textTransform: "uppercase",
    color: "rgba(201,168,76,0.78)",
    marginBottom: 10,
  },
  mediaTypeRow: {
    display: "flex",
    gap: 8,
    marginBottom: 16,
  },
  uploadRemoveButton: {
    position: "absolute",
    top: 8,
    right: 8,
    width: 26,
    height: 26,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    boxShadow: "0 2px 6px rgba(0,0,0,0.4)",
    zIndex: 2,
  },
  input: {
    width: "100%",
    backgroundColor: "#0d1b2a",
    border: "1px solid #3a5a7a",
    color: "#ffffff",
    borderRadius: 8,
    padding: "10px 12px",
    fontFamily: "Georgia, 'Times New Roman', serif",
    // 16px or more: iOS zooms the page into smaller inputs on focus and stays zoomed.
    fontSize: 16,
    boxSizing: "border-box",
  },
  primaryButton: {
    background: "#c9a84c",
    color: "#0d1b2a",
    border: "none",
    borderRadius: 8,
    padding: "10px 18px",
    fontWeight: "bold",
    cursor: "pointer",
    fontFamily: "Georgia, 'Times New Roman', serif",
    whiteSpace: "nowrap",
  },
  secondaryButton: {
    background: "transparent",
    border: "1px solid #3a5a7a",
    color: "#ffffff",
    borderRadius: 8,
    padding: "8px 14px",
    cursor: "pointer",
    fontFamily: "Georgia, 'Times New Roman', serif",
    whiteSpace: "nowrap",
  },
  likeButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: 16,
    background: "transparent",
    border: "1px solid #2c4a68",
    color: "#c7d4e0",
    borderRadius: 999,
    padding: "8px 16px",
    cursor: "pointer",
    fontFamily: "Georgia, 'Times New Roman', serif",
    fontSize: 13,
  },
  likeButtonActive: {
    display: "inline-flex",
    alignItems: "center",
    gap: 16,
    background: "rgba(201,168,76,0.14)",
    border: "1px solid #c9a84c",
    color: "#c9a84c",
    borderRadius: 999,
    padding: "8px 16px",
    cursor: "pointer",
    fontFamily: "Georgia, 'Times New Roman', serif",
    fontSize: 13,
    fontWeight: "bold",
  },
  likeIconButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    border: "none",
    padding: 6,
    margin: 0,
    borderRadius: "50%",
    color: "inherit",
    cursor: "pointer",
    font: "inherit",
  },
  likeCountButton: {
    display: "inline-flex",
    alignItems: "center",
    background: "none",
    border: "none",
    padding: "4px 2px",
    margin: 0,
    cursor: "pointer",
    font: "inherit",
  },
  reportLink: {
    background: "transparent",
    border: "none",
    padding: 0,
    margin: 0,
    color: "#8fa3b8",
    font: "inherit",
    fontSize: 12,
    textDecoration: "underline",
    cursor: "pointer",
  },
  deleteIconButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "transparent",
    border: "none",
    padding: 6,
    margin: 0,
    borderRadius: "50%",
    color: "#8fa3b8",
    cursor: "pointer",
    font: "inherit",
  },
  commentButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    background: "transparent",
    border: "1px solid #2c4a68",
    color: "#c7d4e0",
    borderRadius: 999,
    padding: "8px 16px",
    cursor: "pointer",
    fontFamily: "Georgia, 'Times New Roman', serif",
    fontSize: 13,
  },
  followButton: {
    display: "inline-flex",
    alignItems: "center",
    background: "transparent",
    border: "1px solid #2c4a68",
    color: "#c7d4e0",
    borderRadius: 999,
    padding: "6px 14px",
    cursor: "pointer",
    fontFamily: "Georgia, 'Times New Roman', serif",
    fontSize: 12,
    flexShrink: 0,
  },
  followButtonActive: {
    display: "inline-flex",
    alignItems: "center",
    background: "rgba(201,168,76,0.14)",
    border: "1px solid #c9a84c",
    color: "#c9a84c",
    borderRadius: 999,
    padding: "6px 14px",
    cursor: "pointer",
    fontFamily: "Georgia, 'Times New Roman', serif",
    fontSize: 12,
    fontWeight: "bold",
    flexShrink: 0,
  },
  commentSection: {
    marginTop: 14,
    paddingTop: 14,
    borderTop: "1px solid #1e3a5f",
  },
  commentRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
    padding: "10px 4px",
    borderBottom: "1px solid #1e3a5f",
  },
  commentBody: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    minWidth: 0,
  },
  commentAuthor: {
    fontWeight: "bold",
    fontSize: 13,
    color: "#eef2f6",
  },
  commentText: {
    fontSize: 14,
    lineHeight: 1.5,
    color: "#c7d4e0",
  },
  muted: { color: "#8899aa", fontSize: 13 },
  errorBanner: {
    background: "rgba(255,68,68,0.12)",
    border: "1px solid #ff4444",
    color: "#ff4444",
    borderRadius: 8,
    padding: "10px 14px",
    fontSize: 13,
    marginBottom: 16,
    maxWidth: 420,
    marginLeft: "auto",
    marginRight: "auto",
  },
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(0,0,0,0.6)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
    zIndex: 100,
  },
  modalContent: {
    backgroundColor: "#112236",
    border: "1px solid #1e3a5f",
    borderRadius: 14,
    padding: 20,
    maxWidth: 420,
    width: "100%",
    boxSizing: "border-box",
    color: "#ffffff",
    fontFamily: "Georgia, 'Times New Roman', serif",
  },
};
